import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Event } from '../types/event';
import ExternalEventModal from './ExternalEventModal';
import { formatDate } from '../utils/dateUtils';
import './EventListCard.css';

interface EventListCardProps {
  event: Event;
  isExpired?: boolean;
}

const EventListCard: React.FC<EventListCardProps> = ({ event, isExpired = false }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showExternalModal, setShowExternalModal] = useState(false);

  const isExternalEvent = event.event_ticketing_type === 'external';

  const handleCardClick = (eventId: string) => {
    if (!eventId) {
      console.error('Cannot open checkout: event is missing event_uuid', event);
      return;
    }
    navigate(`/embedded_checkout_page/${eventId}`);
  };

  const handleExternalBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (event.external_ticketing_url) {
      setShowExternalModal(true);
    }
  };

  const handleExternalConfirm = () => {
    if (event.external_ticketing_url) {
      window.open(event.external_ticketing_url, '_blank');
    }
    setShowExternalModal(false);
  };


  return (
    <>
      <ExternalEventModal
        isOpen={showExternalModal}
        onClose={() => setShowExternalModal(false)}
        onConfirm={handleExternalConfirm}
        externalUrl={event.external_ticketing_url || ''}
      />
      
      <div 
        className={`event-card ${event.has_free_tickets ? 'free-event' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
      <div className="event-image-container">
        <img 
          src={event.featured_image?.file_url || "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400&h=240&fit=crop"}
          alt={event.event_title} 
          className="event-image loaded" 
        />
        
        {/* Price Badge */}
        {event.has_free_tickets ? (
          <div className="event-badge free">
            <i className="fas fa-gift"></i> Free
          </div>
        ) : (event.min_price && event.min_price > 0) ? (
          <div className="event-badge price-badge">
            <i className="fas fa-ticket-alt"></i> ${event.min_price.toLocaleString()}{event.has_price_range ? ` - $${event.max_price?.toLocaleString() || 0}` : ''}
          </div>
        ) : null}

        {/* External Event Badge */}
        {isExternalEvent && (
          <div 
            className="event-badge external-badge clickable"
            onClick={handleExternalBadgeClick}
          >
            <i className="fas fa-external-link-alt"></i> External
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="event-gradient-overlay"></div>
        
        {/* content Overlay */}
        <div className="event-content-overlay">
          <div className="event-meta-info">
            <div className="event-location">
              <i className="fas fa-map-marker-alt"></i>
              {event.city}, {event.country}
            </div>
            <div className="event-date">
              <i className="fas fa-calendar"></i>
              {formatDate(event.start, event.timezone)}
            </div>
          </div>
          
          <h3 className="event-title">{event.event_title}</h3>
          
          {/* Buy Ticket Button - visible on hover */}
          {!isExpired && (
            <button 
              className={`buy-ticket-btn-list ${isHovered ? 'visible' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick(event.event_uuid);
              }}
            >
              {event.has_free_tickets ? 'Get Ticket' : 'Buy Ticket'}
            </button>
          )}
        </div>
        
        {/* Hover Overlay */}
        <div className={`event-hover-overlay ${isHovered ? 'active' : ''}`}>
          <div className="hover-actionsless">
             {/* Use CSS to style this, maybe removed view details button to just use the whole card click + buy button at bottom */}
          </div>
          <button 
            className="overlay-btn view-details-btn"
            onClick={() => handleCardClick(event.event_uuid)}
          >
            <i className="fas fa-eye"></i> View details
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default EventListCard;
